import { useEffect } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/auth/AuthProvider";
import { SetupAnalysisResult } from "@/types/agent/setup";
import { ReviewSectionCard } from "./ReviewSectionCard";

interface ConfirmationOwnershipSectionProps {
  analysis: SetupAnalysisResult;
  onChange: (field: keyof SetupAnalysisResult, value: any) => void;
}

export const ConfirmationOwnershipSection = ({
  analysis,
  onChange,
}: ConfirmationOwnershipSectionProps) => {
  const { team, user } = useAuth();

  const isTeamOwner = !!team?.owner_id && team.owner_id === user?.id;
  const showSection = !!team?.owner_id && !isTeamOwner;

  // Team members default to creating genies for the team owner unless they opt out
  useEffect(() => {
    if (showSection && analysis.createForTeamOwner === undefined) {
      onChange("createForTeamOwner", true);
    }
  }, [showSection, analysis.createForTeamOwner, onChange]);

  if (!showSection) {
    return null;
  }

  return (
    <ReviewSectionCard
      title="Ownership"
      description="Choose who owns this genie"
      icon={<HelpCircle className="w-5 h-5" />}
    >
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700 mb-4">
            You're part of a team. You can create this genie as your own or on behalf of the team owner.
          </p>

          <div className="flex items-center justify-between p-3 bg-white rounded border border-gray-200">
            <div className="flex-1">
              <Label className="font-semibold text-gray-900">Create for team owner</Label>
              <p className="text-xs text-gray-600 mt-1">
                This genie will be owned by your team owner and visible to all team members
              </p>
            </div>
            <Switch
              checked={analysis.createForTeamOwner ?? true}
              onCheckedChange={(checked) => onChange("createForTeamOwner", checked)}
              className="ml-4"
            />
          </div>

          {!analysis.createForTeamOwner && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              ℹ️ This genie will be private to you only
            </div>
          )}

          {analysis.createForTeamOwner && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-xs text-green-800">
              ✓ This genie will be owned by the team owner and shared with team members
            </div>
          )}
        </div>
      </div>
    </ReviewSectionCard>
  );
};
